// src/telephony/useIncomingRingtone.ts
//
// ADDITIVE (inbound calling) — an audible ringtone for the incoming-call modal.
//
// The modal previously alerted only visually (a pulsing icon). This hook adds
// the sound: while `active` is true it plays a looping phone-ring cadence
// synthesised with the Web Audio API — no audio asset to bundle, no network
// fetch (CSP-safe), works offline. It stops the instant the call is answered,
// declined, cancelled, or the component unmounts.
//
// Autoplay: the AudioContext is resumed on use; because the user is already
// interacting with the app when a call arrives, the browser permits it. If a
// browser still blocks it, the ring silently no-ops — it never throws.

import { useEffect } from 'react';

export function useIncomingRingtone(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const Ctx: typeof AudioContext | undefined =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;

    let ctx: AudioContext | null = null;
    let osc1: OscillatorNode | null = null;
    let osc2: OscillatorNode | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;

    try {
      ctx = new Ctx();
      void ctx.resume?.();

      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      gain.connect(ctx.destination);

      // North-American-style dual-tone ring (440 Hz + 480 Hz).
      osc1 = ctx.createOscillator();
      osc1.frequency.value = 440;
      osc1.type = 'sine';
      osc1.connect(gain);
      osc2 = ctx.createOscillator();
      osc2.frequency.value = 480;
      osc2.type = 'sine';
      osc2.connect(gain);
      osc1.start();
      osc2.start();

      // Cadence: ~1s ring, ~2s silence, repeating — gated by the gain envelope
      // so the oscillators keep running (cheaper than start/stop churn).
      const ringOnce = () => {
        if (!ctx) return;
        const now = ctx.currentTime;
        const g = gain.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(0.0001, now);
        g.exponentialRampToValueAtTime(0.18, now + 0.05); // fade in
        g.setValueAtTime(0.18, now + 1.0); // hold ~1s
        g.exponentialRampToValueAtTime(0.0001, now + 1.1); // fade out
      };
      ringOnce();
      interval = setInterval(ringOnce, 3000);
    } catch {
      /* audio unavailable — ring silently no-ops */
    }

    return () => {
      if (interval) clearInterval(interval);
      try {
        osc1?.stop();
        osc2?.stop();
      } catch {
        /* already stopped */
      }
      try {
        void ctx?.close();
      } catch {
        /* already closed */
      }
    };
  }, [active]);
}

export default useIncomingRingtone;
